import React, { useContext, useMemo, useRef } from 'react';

import { Button } from '@mui/material';
import { format } from 'date-fns';
import { unparse } from 'papaparse';
import slugify from 'slugify';
import styled from 'styled-components';

import { useAttendanceData } from './useAttendanceData';
import { AttendanceContext, View } from '../../context/AttendanceContext';
import formatStudentName from '../../utils/formatStudentName';
import { getComparator } from '../../utils/sortingUtils';

const StyledButton = styled(Button)`
    margin-left: 1rem;
`;

const AttendanceCsvDownload: React.FC = () => {
    const { loading, t, classInfo, view, sessions, classSessionId, students, studentId } =
        useContext(AttendanceContext);
    const { studentRows, sessionRows, sessionSummaryRows, studentSummaryRows } = useAttendanceData();
    const isScoreable = useMemo(() => {
        switch (view) {
            case View.Student:
            case View.StudentSummary:
                return classInfo.lms_sync_settings?.enable_attendance_grade;
            default:
                return false;
        }
    }, [classInfo, view]);
    const lmsAttendanceTardyScore = useMemo(
        () =>
            (classInfo.class_options?.attendanceGradeTardyScore ||
                classInfo.lms_sync_settings?.attendance_grade_tardy_score ||
                80) / 100,
        [classInfo]
    );

    const csvLink = useRef<HTMLAnchorElement>(null);

    const prepareDataForCsv = (): string => {
        switch (view) {
            case View.Session: {
                const preparedData = studentRows.sort(getComparator('asc', 'sortName')).map((student) => {
                    if (!student.attendance) {
                        return {
                            name: student.name,
                            attendance: 'Absent',
                            tardiness: 'N/A',
                            timeIn: 'N/A',
                            timeOut: 'N/A',
                            duration: 0
                        };
                    }

                    return {
                        name: student.name,
                        attendance: student.attendance ? 'Present' : 'Absent',
                        tardiness: student.tardiness ? 'Late' : 'On Time',
                        timeIn: student.timeIn === '' ? '' : format(new Date(student.timeIn), 'h:mm a'),
                        timeOut: student.timeOut === '' ? '' : format(new Date(student.timeOut), 'h:mm a'),
                        duration: student.duration
                    };
                });

                const parsedData = unparse(preparedData, {
                    header: false,
                    columns: ['name', 'attendance', 'tardiness', 'timeIn', 'timeOut', 'duration']
                });

                const translatedHeaderRow = `${t('student')}, ${t('attendance')}, ${t('tardiness')}, ${t(
                    'time-in'
                )}, ${t('time-out')}, ${t('duration')}\n`;
                return `${translatedHeaderRow}${parsedData}`;
            }
            case View.Student: {
                const preparedData = sessionRows.sort(getComparator('desc', 'date')).map((session) => {
                    if (!session.attendance) {
                        return {
                            date: session.date,
                            attendance: 'Absent',
                            tardiness: 'N/A',
                            timeIn: 'N/A',
                            timeOut: 'N/A',
                            duration: 0,
                            score: '0'
                        };
                    }

                    return {
                        date: session.date,
                        attendance: session.attendance ? 'Present' : 'Absent',
                        tardiness: session.tardiness ? 'Late' : 'On Time',
                        timeIn: session.timeIn === '' ? '' : format(new Date(session.timeIn), 'h:mm a'),
                        timeOut: session.timeOut === '' ? '' : format(new Date(session.timeOut), 'h:mm a'),
                        duration: session.duration,
                        score: session.attendance ? (session.tardiness ? lmsAttendanceTardyScore : '1') : '0'
                    };
                });
                const columns = ['date', 'attendance', 'tardiness', 'timeIn', 'timeOut', 'duration'];
                if (isScoreable) {
                    columns.push('score');
                }
                const parsedData = unparse(preparedData, {
                    header: false,
                    columns
                });
                const headerColumns = ['date', 'attendance', 'tardiness', 'time-in', 'time-out', 'duration'];
                if (isScoreable) {
                    headerColumns.push('score');
                }
                const translatedHeaderRow = headerColumns.map((column) => t(column)).join(',') + '\n';
                return `${translatedHeaderRow}${parsedData}`;
            }
            case View.SessionSummary: {
                const preparedData = sessionSummaryRows.sort(getComparator('desc', 'date')).map((session) => ({
                    date: session.date,
                    absences: session.absences,
                    presences: session.presences,
                    tardies: session.tardies
                }));

                const parsedData = unparse(preparedData, {
                    header: false,
                    columns: ['date', 'presences', 'absences', 'tardies']
                });

                const translatedHeaderRow = `${t('date')}, ${t('presences')}, ${t('absences')}, ${t('tardies')}, ${t(
                    'duration'
                )}\n`;
                return `${translatedHeaderRow}${parsedData}`;
            }
            case View.StudentSummary: {
                const preparedData = studentSummaryRows.sort(getComparator('asc', 'sortName')).map((student) => ({
                    absences: student.absences,
                    name: student.name,
                    presences: student.presences,
                    tardies: student.tardies,
                    score: (
                        ((student.presences - student.tardies + student.tardies * lmsAttendanceTardyScore) /
                            (student.presences + student.absences + student.tardies)) *
                        100
                    ).toFixed(2)
                }));
                const columns = ['name', 'presences', 'absences', 'tardies'];
                if (isScoreable) {
                    columns.push('score');
                }
                const parsedData = unparse(preparedData, {
                    header: false,
                    columns: columns
                });
                const translatedHeaderRow = columns.map((column) => t(column)).join(',') + '\n';
                return `${translatedHeaderRow}${parsedData}`;
            }
            default:
                return '';
        }
    };

    const downloadCsvUrl = (): string => {
        const data = prepareDataForCsv();
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), data], { type: 'text/csv;charset=utf-8;' });
        return URL.createObjectURL(blob);
    };

    const generateFileName = (): string => {
        if (loading) return '';

        const currentClassName = slugify(classInfo.class_name, { replacement: '_' });

        switch (view) {
            case View.Session: {
                const currentSession = sessions.filter((session) => session.session_id === classSessionId)[0];
                // wait for state to finish updating to get current session
                if (currentSession !== undefined) {
                    const currentSessionDate = format(new Date(currentSession.start_time), 'MM_dd_yy'); // TODO: localize date format
                    return `${currentClassName}-${currentSessionDate}-${t('attendance')}.csv`;
                }
                break;
            }
            case View.Student: {
                const currentStudent = students.filter((student) => student.student_id === studentId)[0];
                // wait for state to finish updating to get current student
                if (currentStudent !== undefined) {
                    const currentStudentName = formatStudentName(currentStudent);
                    return `${currentStudentName}-${currentClassName}-${t('attendance')}.csv`;
                }
                break;
            }
            case View.StudentSummary: {
                return `${currentClassName}-${t('student')}_${t('summary')}-${t('attendance')}.csv`;
            }
            case View.SessionSummary: {
                return `${currentClassName}-${t('session')}_${t('summary')}-${t('attendance')}.csv`;
            }
            default:
                break;
        }

        return '';
    };

    return (
        <>
            <StyledButton
                id="attendance-csv-download-btn"
                disabled={loading}
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => csvLink?.current?.click()}
            >
                {t('download')}
            </StyledButton>
            <a
                download={generateFileName()}
                style={{ display: 'none' }}
                target="_self"
                href={downloadCsvUrl()}
                ref={csvLink}
            />
        </>
    );
};

export default AttendanceCsvDownload;
